// ABOUTME: Relation-facing modal that lets a customer generate their own shipping label(s).
// ABOUTME: Picks a destination country + valid shipper profile, collects addresses/dimensions, then POSTs to create labels.
import { Box, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import * as yup from "yup";

import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import ShValidatedInput from "../../theme/inputs/ShValidatedInput";
import ShValidatedInputDropdown from "../../theme/dropdowns/ShValidatedInputDropdown";
import MSelectLabel from "./MSelectLabel";
import { useCreateRelationLabelMutation } from "../../../newapi/order/shipmentsSlice";
import { useGetRelationAddressSelectQuery } from "../../../newapi/relationaddress/relationaddressSlice";
import useDutchStreetLookup from "../../../hooks/useDutchStreetLookup";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import { yupRequired } from "../../../utils/validation";
import { formatPrice } from "../../../utils/helpers";
import { countryCodeNL } from '../../../utils/constants';
import { countriesFromLocale } from '../../../utils/countries';

// Sentinel id for the "Geen" (none) option that clears the address fields.
const NO_ADDRESS_OPTION_ID = 'none';

const emptyAddress = {
    name: "",
    companyName: "",
    street: "",
    houseNumber: "",
    houseNumberAddition: "",
    postalCode: "",
    city: "",
    country: "",
    email: "",
    phone: "",
};

const emptyDimensions = {
    length: "",
    width: "",
    height: "",
    weight: "",
    volume: "",
};

const initialForm = () => ({
    country: countryCodeNL,
    shipperProfileId: null,
    receiver: { ...emptyAddress, country: countryCodeNL },
    dimensions: { ...emptyDimensions },
    reference: "",
    notes: "",
    colliCount: 1,
    amount: 1,
});

// A profile is available for a country when it has no country restriction
// (labelOptions null / countries missing or empty = unrestricted) or when its
// country list includes the selected country. Comparison is case-insensitive.
const isProfileAvailableForCountry = (profile, country) => {
    const list = profile.labelOptions?.countries;
    if (!list || list.length === 0) return true;
    return list.map(c => c.toUpperCase()).includes((country || "").toUpperCase());
};

const MCreateShipment = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const profiles = useMemo(() => props.profiles ?? [], [props.profiles]);

    const [createRelationLabel, { isLoading }] = useCreateRelationLabelMutation();

    const [form, setForm] = useState(initialForm());
    const [fieldErrors, setFieldErrors] = useState({});
    const [result, setResult] = useState(null);
    const [labelsModal, setLabelsModal] = useState({ open: false, labels: [], type: null });

    const language = useSelector((state) => state.settings.language);
    const countries = countriesFromLocale(language);

    // Fetch the relation's saved (standard) addresses. The backend scopes the
    // result to the active relation via the relation_id request header.
    const { data: relationAddresses } = useGetRelationAddressSelectQuery();

    // For a Dutch receiver address, postal code + house number fill in the street and city.
    const streetLookup = useDutchStreetLookup(form.receiver, ({ street, city }) => {
        setForm(prev => ({ ...prev, receiver: { ...prev.receiver, street, city: city || prev.receiver.city } }));
        setFieldErrors(prev => ({ ...prev, "receiver.street": '', "receiver.city": '' }));
    });

    // Index of the currently selected saved address in the dropdown.
    const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);

    // Reset the form and any prior result whenever the modal is bound to a
    // different inbound. Without this a label created for one inbound could carry
    // its stale result/inboundId into the next, linking the shipment to the wrong one.
    useEffect(() => {
        setForm(initialForm());
        setFieldErrors({});
        setResult(null);
        setSelectedAddressIndex(-1);
        setLabelsModal({ open: false, labels: [], type: null });
    }, [props.inboundId]);

    // Prepend a "Geen" option that clears the address fields when selected.
    const relationAddressOptions = useMemo(() => {
        if (!relationAddresses || relationAddresses.length === 0) return [];
        return [
            { id: NO_ADDRESS_OPTION_ID, name: t("none") },
            ...relationAddresses
        ];
    }, [relationAddresses, t]);

    // Fill the receiver fields from a selected saved address, or clear them when
    // the "Geen" option is picked. The receiver country stays tied to the
    // top-level country selector that drives shipper availability.
    const handleRelationAddressChange = (key, val) => {
        const index = relationAddressOptions.findIndex(o => o.id === val);
        setSelectedAddressIndex(index);

        if (val === NO_ADDRESS_OPTION_ID) {
            setForm(prev => ({
                ...prev,
                receiver: { ...emptyAddress, country: prev.receiver.country },
            }));
            setFieldErrors({});
            return;
        }

        const address = relationAddressOptions[index]?.addressObject;
        if (!address) return;

        setForm(prev => ({
            ...prev,
            receiver: {
                ...prev.receiver,
                name: [address.firstName, address.lastName].filter(Boolean).join(" "),
                companyName: address.company ?? "",
                street: address.street ?? "",
                houseNumber: address.houseNumber ?? "",
                houseNumberAddition: address.houseNumberAddition ?? "",
                postalCode: address.postalCode ?? "",
                city: address.city ?? "",
                email: address.email ?? "",
                phone: address.phone ?? "",
            },
        }));
        setFieldErrors({});
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleClose = () => {
        setForm(initialForm());
        setFieldErrors({});
        setResult(null);
        setSelectedAddressIndex(-1);
        setLabelsModal({ open: false, labels: [], type: null });
        props.handleClose();
    };

    // Profiles valid for the currently selected country.
    const availableProfiles = useMemo(
        () => profiles.filter(p => isProfileAvailableForCountry(p, form.country)),
        [profiles, form.country]
    );

    // Options carry a composite display label (name — carrier — price).
    const profileOptions = useMemo(() => availableProfiles.map(p => {
        const price = p.invoiceProduct && p.invoiceProduct.price != null
            ? formatPrice(i18n, p.invoiceProduct.price)
            : "—";
        return {
            ...p,
            label: `${p.name} — ${p.shipper?.name ?? ""} — ${price}`,
        };
    }), [availableProfiles, i18n]);

    const selectedProfile = useMemo(
        () => profiles.find(p => p.id === form.shipperProfileId) ?? null,
        [profiles, form.shipperProfileId]
    );

    const hasAvailableProfiles = availableProfiles.length > 0;

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        setFieldErrors(prev => ({ ...prev, [key]: '' }));
    };

    const handleCountryChange = (key, value) => {
        setForm(prev => {
            // If the selected profile is no longer valid for the new country, clear it.
            const stillValid = prev.shipperProfileId != null &&
                profiles.filter(p => isProfileAvailableForCountry(p, value))
                    .some(p => p.id === prev.shipperProfileId);
            return {
                ...prev,
                country: value,
                shipperProfileId: stillValid ? prev.shipperProfileId : null,
                receiver: { ...prev.receiver, country: value },
            };
        });
    };

    const handleAddressChange = (section) => (key, value) => {
        setForm(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
        setFieldErrors(prev => ({ ...prev, [`${section}.${key}`]: '' }));
    };

    const handleDimensionChange = (key, value) => {
        setForm(prev => ({ ...prev, dimensions: { ...prev.dimensions, [key]: value } }));
        setFieldErrors(prev => ({ ...prev, [`dimensions.${key}`]: '' }));
    };

    // Selecting a profile prefills the dimension fields with 50% of its max.
    const handleProfileChange = (key, value) => {
        const profile = profiles.find(p => p.id === value) ?? null;
        setForm(prev => ({
            ...prev,
            shipperProfileId: value,
            dimensions: profile
                ? { ...prev.dimensions, ...defaultDimensionsFor(profile) }
                : prev.dimensions,
        }));
        setFieldErrors(prev => ({ ...prev, shipperProfileId: '' }));
    };

    // Half of a max value, used to prefill a dimension field when a profile is picked.
    const halfOf = (max, decimals = 0) => (max ? String(Number((max * 0.5).toFixed(decimals))) : "");

    // 50%-of-max default values for the dimension fields. Units mirror the
    // max-limit display: size in cm (mm / 10), weight in kg (grams / 1000).
    const defaultDimensionsFor = (profile) => ({
        length: halfOf(profile.labelMaxLength / 10),
        width: halfOf(profile.labelMaxWidth / 10),
        height: halfOf(profile.labelMaxHeight / 10),
        weight: halfOf(profile.labelMaxWeight / 1000, 2),
        volume: "",
    });

    const addressSchema = yup.object().shape({
        name: yupRequired('name', t),
        street: yupRequired('street', t),
        houseNumber: yupRequired('house_nr', t),
        postalCode: yupRequired('postal', t),
        city: yupRequired('city', t),
        country: yupRequired('country', t),
    });

    const runSchema = async (schema, data, prefix) => {
        try {
            await schema.validate(data, { abortEarly: false });
            return {};
        } catch (err) {
            if (err.name === 'ValidationError') {
                return err.inner.reduce((acc, e) => ({ ...acc, [`${prefix}.${e.path}`]: e.message }), {});
            }
            throw err;
        }
    };

    const validateDimensions = () => {
        const errors = {};
        if (!selectedProfile) return errors;
        const checks = [
            { key: 'length', max: selectedProfile.labelMaxLength },
            { key: 'width', max: selectedProfile.labelMaxWidth },
            { key: 'height', max: selectedProfile.labelMaxHeight },
            { key: 'weight', max: selectedProfile.labelMaxWeight / 1000 },
            { key: 'volume', max: selectedProfile.labelMaxVolume },
        ];
        checks.forEach(({ key, max }) => {
            const raw = form.dimensions[key];
            if (raw === "" || raw === null || typeof raw === "undefined") return;
            const num = Number(raw);
            if (isNaN(num) || num <= 0) {
                errors[`dimensions.${key}`] = t("amount_greater_than_zero");
            } else if (max && num > max) {
                errors[`dimensions.${key}`] = t("value_exceeds_max", { max });
            }
        });
        return errors;
    };

    const validateCounts = () => {
        const errors = {};
        const amount = Number(form.amount);
        if (isNaN(amount) || amount < 1 || amount > 50) {
            errors.amount = t("amount_between_1_and_50");
        }
        const colli = Number(form.colliCount);
        if (isNaN(colli) || colli < 1) {
            errors.colliCount = t("amount_greater_than_zero");
        }
        return errors;
    };

    const buildAddress = (a) => ({
        name: a.name,
        companyName: a.companyName,
        street: a.street,
        houseNumber: a.houseNumber,
        houseNumberAddition: a.houseNumberAddition,
        postalCode: a.postalCode,
        city: a.city,
        country: (a.country || "").toUpperCase(),
        email: a.email,
        phone: a.phone,
    });

    const openSingleLabel = (content, type) => {
        let url = "";
        if (type === 'pdf') {
            const decodedData = atob(content);
            const arrayBuffer = new ArrayBuffer(decodedData.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < decodedData.length; i++) {
                uint8Array[i] = decodedData.charCodeAt(i);
            }
            const blob = new Blob([uint8Array], { type: 'application/pdf' });
            url = URL.createObjectURL(blob);
        } else if (type === 'link') {
            url = content;
        }
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
            setTimeout(() => { window.URL.revokeObjectURL(url); }, 500);
        } else {
            window.URL.revokeObjectURL(url);
        }
    };

    const handleSubmit = async () => {
        if (isLoading) return;
        if (!selectedProfile) return;

        setFieldErrors({});

        const receiverErrors = await runSchema(addressSchema, form.receiver, 'receiver');
        const dimensionErrors = validateDimensions();
        const countErrors = validateCounts();
        const allErrors = { ...receiverErrors, ...dimensionErrors, ...countErrors };

        if (Object.keys(allErrors).length > 0) {
            setFieldErrors(allErrors);
            return;
        }

        // Only include dimension fields the user actually filled in; blanks default server-side.
        const dimensions = ['length', 'width', 'height', 'weight', 'volume'].reduce((acc, key) => {
            const raw = form.dimensions[key];
            if (raw !== "" && raw !== null && typeof raw !== "undefined") {
                acc[key] = Number(raw);
            }
            return acc;
        }, {});

        const payload = {
            shipperProfileId: selectedProfile.id,
            receiver: buildAddress(form.receiver),
            ...(Object.keys(dimensions).length > 0 ? { dimensions } : {}),
            ...(form.reference ? { reference: form.reference } : {}),
            ...(form.notes ? { notes: form.notes } : {}),
            colliCount: Number(form.colliCount) || 1,
            amount: Number(form.amount) || 1,
            ...(props.inboundId ? { inboundId: props.inboundId } : {})
        };

        try {
            const data = await createRelationLabel(payload).unwrap();

            const labels = data.labels ?? [];
            if (labels.length > 1) {
                setLabelsModal({ open: true, labels, type: data.labelType });
            } else if (labels.length === 1) {
                openSingleLabel(labels[0], data.labelType);
            }

            const barcodes = (data.barcode ?? "").split(",").map(b => b.trim()).filter(Boolean);
            setResult({ barcodes });

            showToastMessagePostRequest(t, data);
            props.onCreated?.(data);
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        }
    };

    const rowSx = {
        width: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 7,
    };
    const inputBoxSx = {
        flex: 1,
        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`,
        borderRadius: 4,
        display: "flex",
        height: 32,
    };

    const TextField = ({ label, section, name, value, error, type, placeholder, loading }) => (
        <Box sx={rowSx}>
            <Box sx={{ width: 120 }}>
                <Text>{label}</Text>
            </Box>
            <Box sx={inputBoxSx}>
                <ShValidatedInput
                    name={name}
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    loading={loading}
                    onChange={section ? handleAddressChange(section) : handleChange}
                    error={error}
                />
            </Box>
        </Box>
    );

    const DimensionField = ({ label, name, unit }) => (
        <Box sx={rowSx}>
            <Box sx={{ width: 120 }}>
                <Text>{label}</Text>
            </Box>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={inputBoxSx}>
                    <ShValidatedInput
                        name={name}
                        type="number"
                        value={form.dimensions[name]}
                        onChange={handleDimensionChange}
                        error={fieldErrors[`dimensions.${name}`]}
                    />
                </Box>
                <Box sx={{ width: 24 }}>
                    <Text light>{unit}</Text>
                </Box>
            </Box>
        </Box>
    );

    const renderAddressFields = (section, data) => (
        <>
            {TextField({ label: t("name"), section, name: "name", value: data.name, error: fieldErrors[`${section}.name`] })}
            {TextField({ label: t("company_name"), section, name: "companyName", value: data.companyName, error: fieldErrors[`${section}.companyName`] })}
            {TextField({ label: t("postal"), section, name: "postalCode", value: data.postalCode, error: fieldErrors[`${section}.postalCode`] })}
            {TextField({ label: t("house_nr"), section, name: "houseNumber", value: data.houseNumber, error: fieldErrors[`${section}.houseNumber`] })}
            {TextField({ label: t("house_nr_extra"), section, name: "houseNumberAddition", value: data.houseNumberAddition, error: fieldErrors[`${section}.houseNumberAddition`] })}
            {TextField({ label: t("street"), section, name: "street", value: data.street, error: fieldErrors[`${section}.street`], loading: streetLookup.isLoading, placeholder: streetLookup.isLoading ? t("address_lookup_searching") : undefined })}
            {streetLookup.notFound &&
                <Box sx={rowSx}>
                    <Box sx={{ width: 120 }} />
                    <Box sx={{ flex: 1, pl: 2 }}>
                        <Text light>{t("address_lookup_not_found")}</Text>
                    </Box>
                </Box>
            }
            {TextField({ label: t("city"), section, name: "city", value: data.city, error: fieldErrors[`${section}.city`], loading: streetLookup.isLoading, placeholder: streetLookup.isLoading ? t("address_lookup_searching") : undefined })}
            {TextField({ label: t("email"), section, name: "email", value: data.email, error: fieldErrors[`${section}.email`] })}
            {TextField({ label: t("phone"), section, name: "phone", value: data.phone, error: fieldErrors[`${section}.phone`] })}
        </>
    );

    const maxLimitsText = selectedProfile
        ? t("max_limits", {
            length: selectedProfile.labelMaxLength / 10,
            width: selectedProfile.labelMaxWidth / 10,
            height: selectedProfile.labelMaxHeight / 10,
            weight: selectedProfile.labelMaxWeight / 1000,
        })
        : "";

    const submitDisabled = isLoading || !selectedProfile || !hasAvailableProfiles;

    return (
        <SHModal
            open={props.open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1
                }}>
                <Subheading>{t("create_shipment")}</Subheading>
            </Box>

            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    maxHeight: "60vh",
                    overflowY: "auto",
                }}
            >
                {result ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Text bold>{t("shipment_created")}</Text>
                        {result.barcodes.length > 0 &&
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Text light>{t("tracking_number")}</Text>
                                {result.barcodes.map((barcode, index) => (
                                    <Text key={index} bold>{barcode}</Text>
                                ))}
                            </Box>
                        }
                    </Box>
                ) : (
                    <>
                        {/* Country drives which profiles are valid */}
                        <Box sx={rowSx}>
                            <Box sx={{ width: 120 }}>
                                <Text>{t("country")}</Text>
                            </Box>
                            <Box sx={inputBoxSx}>
                                <ShValidatedInputDropdown
                                    name="country"
                                    displayName="name"
                                    changeField="alpha2"
                                    options={countries ?? null}
                                    noSelection={t("choose_option")}
                                    onChange={handleCountryChange}
                                    selected={countries?.findIndex(c => c.alpha2 === form.country) ?? -1}
                                    error={fieldErrors.country}
                                    searchOptions={{
                                        enabled:true,
                                        placeholder:t("search")
                                    }}
                                />
                            </Box>
                        </Box>

                        {!hasAvailableProfiles ? (
                            <Box sx={{ paddingY: 2 }}>
                                <Text light>
                                    {t("no_shipping_option_for_country", { country: (form.country || "").toUpperCase() })}
                                </Text>
                            </Box>
                        ) : (
                            <>
                                {/* Profile selector */}
                                <Box sx={rowSx}>
                                    <Box sx={{ width: 120 }}>
                                        <Text>{t("shipper")}</Text>
                                    </Box>
                                    <Box sx={inputBoxSx}>
                                        <ShValidatedInputDropdown
                                            name="shipperProfileId"
                                            displayName="label"
                                            changeField="id"
                                            options={profileOptions}
                                            noSelection={t("choose_option")}
                                            onChange={handleProfileChange}
                                            selected={profileOptions.findIndex(p => p.id === form.shipperProfileId)}
                                            error={fieldErrors.shipperProfileId}
                                        />
                                    </Box>
                                </Box>

                                {selectedProfile &&
                                    <Box sx={{ paddingLeft: "176px", display: "flex", flexDirection: "column", gap: 0.5 }}>
                                        <Text bold>{maxLimitsText}</Text>
                                        <Text light>{t("dimensions_autofilled")}</Text>
                                    </Box>
                                }

                                {selectedProfile &&
                                    <>
                                        {/* Dimensions & weight, prefilled with 50% of the profile max */}
                                        {DimensionField({ label: t("length"), name: "length", unit: "cm" })}
                                        {DimensionField({ label: t("width"), name: "width", unit: "cm" })}
                                        {DimensionField({ label: t("height"), name: "height", unit: "cm" })}
                                        {DimensionField({ label: t("weight"), name: "weight", unit: "kg" })}

                                        {/* Receiver address (country is the top selector). The sender is
                                            derived server-side from the warehouse + relation and cannot be set here. */}
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                            <Text bold>{t("receiver")}</Text>
                                            {relationAddressOptions.length > 0 &&
                                                <Box sx={rowSx}>
                                                    <Box sx={{ width: 120 }}>
                                                        <Text>{t("standard_address")}</Text>
                                                    </Box>
                                                    <Box sx={inputBoxSx}>
                                                        <ShValidatedInputDropdown
                                                            name="relationAddress"
                                                            displayName="name"
                                                            changeField="id"
                                                            options={relationAddressOptions}
                                                            noSelection={t("choose_option")}
                                                            onChange={handleRelationAddressChange}
                                                            selected={selectedAddressIndex}
                                                            closeOnSelection
                                                            searchOptions={{
                                                                enabled: true,
                                                                placeholder: t("search")
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            }
                                            {renderAddressFields("receiver", form.receiver)}
                                        </Box>

                                        {/* Amount / reference / notes */}
                                        <Box sx={rowSx}>
                                            <Box sx={{ width: 120 }}><Text>{t("amount")}</Text></Box>
                                            <Box sx={inputBoxSx}>
                                                <ShValidatedInput name="amount" type="number" value={form.amount} onChange={handleChange} error={fieldErrors.amount} />
                                            </Box>
                                        </Box>
                                        <Box sx={rowSx}>
                                            <Box sx={{ width: 120 }}><Text>{t("reference")}</Text></Box>
                                            <Box sx={inputBoxSx}>
                                                <ShValidatedInput name="reference" value={form.reference} onChange={handleChange} error={fieldErrors.reference} />
                                            </Box>
                                        </Box>
                                        <Box sx={rowSx}>
                                            <Box sx={{ width: 120 }}><Text>{t("notes")}</Text></Box>
                                            <Box sx={inputBoxSx}>
                                                <ShValidatedInput name="notes" value={form.notes} onChange={handleChange} error={fieldErrors.notes} />
                                            </Box>
                                        </Box>
                                    </>
                                }
                            </>
                        )}
                    </>
                )}
            </Box>

            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    justifyContent: "space-between",
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>
                    {result ? t("close") : t("cancel")}
                </Shbutton>
                {!result &&
                    <ButtonGroup version={2}>
                        <Shbutton isLoading={isLoading} disabled={submitDisabled} className={"group-btn"} color={"green"}
                            variant="contained"
                            onClick={handleSubmit}
                        >{t("create")}</Shbutton>
                    </ButtonGroup>
                }
            </Box>

            <MSelectLabel
                open={labelsModal.open}
                handleClose={() => setLabelsModal({ open: false, labels: [], type: null })}
                labels={labelsModal.labels}
                type={labelsModal.type}
            />
        </SHModal>
    );
};

export default MCreateShipment;
